import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigation } from 'react-router-dom'
import { Home, Package, LogOut, BarChart3, User, Anchor, PlusCircle, Settings, UsersRound } from 'lucide-react'
import { createClient } from '@/lib/auth/client'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { LoadingState } from '@/src/components/LoadingState'
import { getHelmSessionUser, getMainSiteLoginUrl, type HelmSessionUser } from '@/src/lib/auth'

const mainSiteUrl = process.env.NEXT_PUBLIC_MAIN_SITE_URL || 'https://tarifix.xtellar.app'

const menuItems = [
  { title: 'Dashboard', icon: Home, url: '/', match: 'exact' },
  { title: 'Consignments', icon: Package, url: '/consignments', match: 'section' },
  { title: 'Customers', icon: User, url: '/customers', match: 'section' },
  { title: 'Users', icon: UsersRound, url: '/settings/users', match: 'section' },
  { title: 'Settings', icon: Settings, url: '/settings', match: 'exact' },
]

const createItems = [
  { title: 'New Consignment', icon: PlusCircle, url: '/consignments/new' },
  { title: 'New Customer', icon: PlusCircle, url: '/customers/new' },
]

function isMenuItemActive(pathname: string, item: { url: string; match?: string }) {
  if (item.match === 'section') {
    if (pathname === `${item.url}/new`) {
      return false
    }

    return pathname === item.url || pathname.startsWith(`${item.url}/`)
  }

  return pathname === item.url
}

function RouteProgress() {
  const navigation = useNavigation()

  if (navigation.state === 'idle') {
    return null
  }

  return (
    <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-muted">
      <div className="h-full w-1/3 animate-[route-progress_1s_ease-in-out_infinite] bg-primary" />
    </div>
  )
}

function AuthGate({ children }: { children: (user: HelmSessionUser) => ReactNode }) {
  const [user, setUser] = useState<HelmSessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    getHelmSessionUser()
      .then((sessionUser) => {
        if (cancelled) return

        if (!sessionUser) {
          window.location.href = getMainSiteLoginUrl()
          return
        }

        setUser(sessionUser)
      })
      .catch(() => {
        if (!cancelled) {
          window.location.href = getMainSiteLoginUrl()
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (loading || !user) {
    return <LoadingState label="Checking Helm session..." />
  }

  return <>{children(user)}</>
}

export function AppShell() {
  const location = useLocation()
  const authClient = useMemo(() => createClient(), [])

  const handleLogout = async () => {
    await authClient.auth.signOut()
    window.location.href = `${mainSiteUrl}/login`
  }

  return (
    <AuthGate>
      {() => (
        <SidebarProvider>
          <div className="flex min-h-screen w-full">
            <Sidebar>
              <SidebarContent>
                <SidebarGroup>
                  <SidebarGroupLabel className="px-4 py-6 text-xl font-bold">
                    <Link to="/" className="flex items-center gap-2">
                      <Anchor className="h-5 w-5" />
                      Helm
                    </Link>
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {menuItems.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            asChild
                            isActive={isMenuItemActive(location.pathname, item)}
                          >
                            <Link to={item.url}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <Separator />
                <SidebarGroup>
                  <SidebarGroupLabel>Create</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {createItems.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            asChild
                            isActive={location.pathname === item.url}
                          >
                            <Link to={item.url}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <Separator />
                <SidebarGroup>
                  <SidebarGroupLabel>Resources</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild>
                          <Link to="/docs">
                            <BarChart3 className="h-4 w-4" />
                            <span>Documentation</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </SidebarContent>
              <SidebarFooter className="border-t p-4">
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="w-full justify-start"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </SidebarFooter>
            </Sidebar>

            <div className="flex min-w-0 flex-1 flex-col">
              <header className="relative border-b bg-background">
                <div className="flex h-16 items-center gap-4 px-6">
                  <SidebarTrigger />
                  <div className="ml-auto flex items-center gap-4">
                    <ThemeToggle />
                    <Avatar>
                      <AvatarFallback>
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>
                <RouteProgress />
              </header>
              <main className="flex-1 bg-muted/40 p-6">
                <Outlet />
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}
    </AuthGate>
  )
}
